import { useEffect, useMemo, useRef, useState } from 'react';

import { useLazyQuery, useQuery } from '@apollo/client';
import { Box, Grid, Stack, Typography } from '@mui/material';
import { Hierarchy } from '@prisma/client';
import { useField, useFormikContext } from 'formik';
import { Invoice, InvoiceItem } from 'web/types/graphql';

import { Card, Spacer } from 'src/components';
import { CustomRadioGroup, NPIAutoComplete } from 'src/components/FormikComponents';
import { AutoCompleteOptionType } from 'src/components/FormikComponents/NPIAutoComplete';
import { GET_USER_SALES_INVOICES } from 'src/graphql/queries/getUserSalesInvoices.query';
import { GET_MERCHANTS } from 'src/graphql/queries/merchants.query';
import MerchantSelectFields from 'src/pages/AddSalesInvoicePage/components/MerchantSelectFields';
import { formatDate, onError } from 'src/utils/utils';

const INVOICE_TO_FIELD_NAME = 'invoiceTo';
const INVOICE_FIELD_NAME = 'invoice';

export interface MerchantOptionTypes {
    label: string;
    value: number;
}

interface CreditNoteDetailsProps {
    setSelectedInvoiceTo: (selectedMerchant: MerchantOptionTypes) => void;
}

export const updateFormEditedRefOnDirty = ({
    dirty,
    prevSelectedInvoice,
    selectedInvoice,
    formEditedRef,
}) => {
    // We need to update formEditedRef when the form becomes dirty from user interaction,
    // not from initial values or selection changes
    if (dirty && prevSelectedInvoice === selectedInvoice) {
        formEditedRef.current = true;
    }
};

// Only apply the invoice data if:
// 1. We have a selected invoice AND
// 2. Either it's a fresh selection (different from previous) OR form has not been manually edited
// 3. And we're not currently submitting the form
// Reset the form edited state when changing invoices
export const resetFormEditedStateOnInvoiceChange = (
    prevSelectedInvoice,
    selectedInvoice,
    formEditedRef
) => {
    if (prevSelectedInvoice !== selectedInvoice && selectedInvoice) {
        formEditedRef.current = false;
    }
};

export const CreditNoteDetails = ({ setSelectedInvoiceTo }: CreditNoteDetailsProps) => {
    const [salesInvoiceList, setSalesInvoiceList] = useState([]);
    const [selectedMerchant, setSelectedMerchant] = useState(null);
    const [{ value: isInvoiceSelected }, _, { setValue: setIsInvoiceSelected }] =
        useField('isInvoiceSelected');
    const [selectedMerchantAccountNumber, setSelectedMerchantAccountNumber] = useState(null);

    const formEditedRef = useRef(false);

    const { setFieldValue, dirty, isSubmitting } = useFormikContext();
    const [{ value: invoiceToType }] = useField(`${INVOICE_TO_FIELD_NAME}Type`);
    const [{ value: selectedInvoice }] = useField<AutoCompleteOptionType>(INVOICE_FIELD_NAME);

    const { data: merchantsData } = useQuery(GET_MERCHANTS, {
        onError,
    });

    const [getUserSalesInvoices, { loading: fetchingSalesInvoices, data: salesInvoicesData }] =
        useLazyQuery(GET_USER_SALES_INVOICES, {
            fetchPolicy: 'cache-and-network',
            onError,
            onCompleted: (salesInvoices) => {
                const invoiceList = (salesInvoices?.getUserSalesInvoices ?? []).map(
                    ({ id, status, sageInvoiceNumber }) => {
                        return {
                            value: id,
                            label: `Sales Invoice - ${sageInvoiceNumber} `,
                            status: status.toLowerCase().replace('_', ' '),
                        };
                    }
                );
                setSalesInvoiceList(invoiceList);
            },
        });

    const prevSelectedInvoiceRef = useRef(selectedInvoice);

    useEffect(() => {
        const prevSelectedInvoice = prevSelectedInvoiceRef.current;

        updateFormEditedRefOnDirty({
            dirty,
            prevSelectedInvoice,
            selectedInvoice,
            formEditedRef,
        });

        resetFormEditedStateOnInvoiceChange(prevSelectedInvoice, selectedInvoice, formEditedRef);

        if (
            selectedInvoice &&
            (prevSelectedInvoice !== selectedInvoice || !formEditedRef.current) &&
            !isSubmitting
        ) {
            setIsInvoiceSelected(true);
            const invoice: Invoice = (salesInvoicesData?.getUserSalesInvoices ?? []).find(
                (element) => {
                    return element.id === selectedInvoice.value;
                }
            );

            const invoiceItems = (invoice?.invoiceItems ?? []).map(
                ({
                    netAmount,
                    quantity,
                    description,
                    ledgerAccount,
                    totalNet,
                    totalVat,
                    lineTotal,
                }: InvoiceItem) => {
                    return {
                        netAmount,
                        quantity,
                        description,
                        ledgerAccount,
                        totalNet,
                        totalVat,
                        lineTotal,
                    };
                }
            );

            setFieldValue('creditNoteItems', invoiceItems);
        } else if (!selectedInvoice && !isSubmitting) {
            if (prevSelectedInvoice) {
                setFieldValue('creditNoteItems', []);
            }

            if (isInvoiceSelected) {
                setIsInvoiceSelected(false);
            }

            formEditedRef.current = false;
        }
        prevSelectedInvoiceRef.current = selectedInvoice;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        isInvoiceSelected,
        salesInvoicesData?.getUserSalesInvoices,
        selectedInvoice,
        setFieldValue,
        setIsInvoiceSelected,
        isSubmitting,
    ]);

    useEffect(() => {
        if (selectedMerchant) {
            setSelectedInvoiceTo(selectedMerchant);
        }
    }, [selectedMerchant, setSelectedInvoiceTo]);

    // This effect runs on the second render onwards to prevent initial form values from marking the form as edited
    const isInitialRenderRef = useRef(true);
    useEffect(() => {
        if (isInitialRenderRef.current) {
            isInitialRenderRef.current = false;
            return;
        }

        if (dirty && !formEditedRef.current) {
            formEditedRef.current = true;
        }
    }, [dirty]);

    const handleMerchantSelection = (value) => {
        if (value?.value) {
            const merchant = merchantsData?.merchants?.data.find((merchantItem) => {
                return merchantItem.id === value.value;
            });
            if (merchant) {
                setSelectedMerchant({
                    label: merchant.merchantName ?? merchant.tradingName,
                    value: merchant.id,
                });
                setSelectedMerchantAccountNumber({
                    label: merchant.merchantAccountNumber,
                    value: merchant.id,
                });

                fetchInvoices(merchant);
            }
        } else {
            setSelectedMerchant(null);
            setSelectedMerchantAccountNumber(null);

            setFieldValue('merchant', null);
            setFieldValue('merchantAccountNumber', null);
        }
    };

    const fetchInvoices = (merchant) => {
        setFieldValue(INVOICE_FIELD_NAME, null);
        if (merchant) {
            getUserSalesInvoices({
                variables: { id: merchant.id, invoiceToType },
            });
        }
    };

    const merchantOptions = useMemo(() => {
        return (merchantsData?.merchants?.data ?? []).map(({ id, merchantName, tradingName }) => {
            return {
                label: merchantName ?? tradingName,
                value: id,
            };
        });
    }, [merchantsData]);

    const merchantAccountNumberOptions = useMemo(() => {
        return (merchantsData?.merchants?.data ?? []).map(({ id, merchantAccountNumber }) => {
            return {
                label: merchantAccountNumber,
                value: id,
            };
        });
    }, [merchantsData]);

    return (
        <Card gap="36px">
            <Typography fontSize="20px" fontWeight="500" lineHeight="30px" color="#1d1d1b">
                Credit Note Details
            </Typography>

            <Grid container>
                <Grid item xs={6}>
                    <CustomRadioGroup
                        name={`${INVOICE_TO_FIELD_NAME}Type`}
                        options={[{ value: Hierarchy.MERCHANT, label: 'Merchant' }]}
                        label="Credit to"
                    />
                </Grid>

                <Grid item xs={6}>
                    <Typography>
                        <strong>Date:</strong> {formatDate({ date: new Date() })}
                    </Typography>
                </Grid>
            </Grid>
            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Box>
                        <Spacer padding={0.6} />
                        <MerchantSelectFields
                            merchant={selectedMerchant}
                            merchantAccountNumber={selectedMerchantAccountNumber}
                            merchantOptions={merchantOptions}
                            merchantAccountNumberOptions={merchantAccountNumberOptions}
                            handleChangeMerchant={handleMerchantSelection}
                            handleChangeMerchantAccountNumber={handleMerchantSelection}
                        />
                    </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Stack>
                        <NPIAutoComplete
                            header={
                                fetchingSalesInvoices
                                    ? 'Fetching Sales Invoices...'
                                    : 'Linked to Invoice'
                            }
                            name={INVOICE_FIELD_NAME}
                            options={salesInvoiceList}
                            disabled={fetchingSalesInvoices}
                            placeholder={
                                fetchingSalesInvoices
                                    ? 'Fetching Sales Invoices...'
                                    : 'Select Invoice'
                            }
                        />
                    </Stack>
                </Grid>
            </Grid>
        </Card>
    );
};
export default CreditNoteDetails;
